import React, { useState } from 'react';
import { BookOpen, Search, TrendingUp, DollarSign, BarChart3, Globe, Calendar, Award, ChevronDown, ChevronUp } from 'lucide-react';

export default function StockEducation() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedTerm, setExpandedTerm] = useState(null);

  // Stock terminology database
  const stockTerms = [
    // Basic Terms
    {
      category: 'basic',
      term: 'Stock (주식)',
      korean: '주식',
      definition: 'A type of security that represents ownership in a corporation and represents a claim on part of the corporation\'s assets and earnings.',
      koreanDef: '회사의 소유권을 나타내는 증권으로, 회사 자산과 수익의 일부에 대한 권리를 의미합니다.',
      example: 'If you own 100 shares of Apple stock, you own a small part of Apple Inc.'
    },
    {
      category: 'basic',
      term: 'Share (주)',
      korean: '주',
      definition: 'A single unit of stock.',
      koreanDef: '주식의 최소 단위입니다.',
      example: 'I bought 10 shares of Tesla at $250 per share.'
    },
    {
      category: 'basic',
      term: 'Ticker Symbol (티커)',
      korean: '티커 심볼',
      definition: 'A unique series of letters representing a particular stock on an exchange.',
      koreanDef: '거래소에서 특정 주식을 나타내는 고유한 문자 기호입니다.',
      example: 'AAPL is the ticker symbol for Apple Inc.'
    },
    {
      category: 'basic',
      term: 'Portfolio (포트폴리오)',
      korean: '포트폴리오',
      definition: 'A collection of financial investments like stocks, bonds, and cash.',
      koreanDef: '주식, 채권, 현금 등 여러 금융 투자 자산의 모음입니다.',
      example: 'My portfolio includes stocks from tech, healthcare, and energy sectors.'
    },

    // Trading Terms
    {
      category: 'trading',
      term: 'Bull Market (강세장)',
      korean: '불 마켓 / 강세장',
      definition: 'A market condition where prices are rising or expected to rise.',
      koreanDef: '가격이 상승하거나 상승할 것으로 예상되는 시장 상황입니다.',
      example: 'We\'ve been in a bull market since 2020.'
    },
    {
      category: 'trading',
      term: 'Bear Market (약세장)',
      korean: '베어 마켓 / 약세장',
      definition: 'A market condition where prices are falling, typically by 20% or more.',
      koreanDef: '가격이 하락하는 시장 상황으로, 일반적으로 20% 이상 하락한 상태를 의미합니다.',
      example: 'The 2022 bear market saw many tech stocks lose significant value.'
    },
    {
      category: 'trading',
      term: 'Market Order (시장가 주문)',
      korean: '마켓 오더',
      definition: 'An order to buy or sell immediately at the current market price.',
      koreanDef: '현재 시장 가격으로 즉시 매수 또는 매도하는 주문입니다.',
      example: 'I placed a market order to buy 50 shares immediately.'
    },
    {
      category: 'trading',
      term: 'Limit Order (지정가 주문)',
      korean: '리미트 오더',
      definition: 'An order to buy or sell at a specific price or better.',
      koreanDef: '특정 가격 이하로 매수하거나 이상으로 매도하는 주문입니다.',
      example: 'I set a limit order to buy at $100 or lower.'
    },
    {
      category: 'trading',
      term: 'Day Trading (데이 트레이딩)',
      korean: '데이 트레이딩',
      definition: 'Buying and selling stocks within the same trading day.',
      koreanDef: '같은 거래일 내에 주식을 매수하고 매도하는 거래 방식입니다.',
      example: 'Day traders often make multiple trades in a single day.'
    },
    {
      category: 'trading',
      term: 'Swing Trading (스윙 트레이딩)',
      korean: '스윙 트레이딩',
      definition: 'Holding stocks for several days to weeks to profit from price swings.',
      koreanDef: '며칠에서 몇 주 동안 주식을 보유하며 가격 변동을 통해 수익을 얻는 방식입니다.',
      example: 'Swing traders look for short to medium-term trends.'
    },

    // Financial Metrics
    {
      category: 'metrics',
      term: 'Market Cap (시가총액)',
      korean: '마켓 캡 / 시가총액',
      definition: 'Total market value of a company\'s outstanding shares.',
      koreanDef: '회사의 발행 주식 총액의 시장 가치입니다.',
      example: 'Apple\'s market cap is over $3 trillion.'
    },
    {
      category: 'metrics',
      term: 'P/E Ratio (주가수익비율)',
      korean: 'P/E 레이쇼',
      definition: 'Price-to-Earnings ratio - stock price divided by earnings per share.',
      koreanDef: '주가를 주당순이익으로 나눈 비율로, 주식의 가치 평가에 사용됩니다.',
      example: 'A P/E ratio of 20 means investors pay $20 for every $1 of earnings.'
    },
    {
      category: 'metrics',
      term: 'EPS (주당순이익)',
      korean: 'EPS / 주당순이익',
      definition: 'Earnings Per Share - company\'s profit divided by outstanding shares.',
      koreanDef: '회사의 순이익을 발행 주식 수로 나눈 값입니다.',
      example: 'If a company earned $1 million with 100,000 shares, EPS is $10.'
    },
    {
      category: 'metrics',
      term: 'Dividend (배당금)',
      korean: '디비던드 / 배당금',
      definition: 'A portion of company profits paid to shareholders.',
      koreanDef: '회사가 주주들에게 지급하는 이익의 일부입니다.',
      example: 'Apple pays quarterly dividends to its shareholders.'
    },
    {
      category: 'metrics',
      term: 'Dividend Yield (배당수익률)',
      korean: '디비던드 일드',
      definition: 'Annual dividend payment divided by stock price, expressed as percentage.',
      koreanDef: '연간 배당금을 주가로 나눈 백분율입니다.',
      example: 'A stock priced at $100 with $4 annual dividend has 4% yield.'
    },
    {
      category: 'metrics',
      term: 'ROE (자기자본이익률)',
      korean: 'ROE',
      definition: 'Return on Equity - measures profitability relative to shareholders\' equity.',
      koreanDef: '자기자본 대비 수익률을 나타냅니다.',
      example: 'An ROE of 15% means the company generates $0.15 profit per $1 of equity.'
    },

    // Market Concepts
    {
      category: 'market',
      term: 'IPO (기업공개)',
      korean: 'IPO / 기업공개',
      definition: 'Initial Public Offering - when a company first sells shares to the public.',
      koreanDef: '회사가 처음으로 주식을 일반 대중에게 판매하는 것입니다.',
      example: 'Airbnb went public through an IPO in December 2020.'
    },
    {
      category: 'market',
      term: 'Stock Split (주식분할)',
      korean: '스톡 스플릿',
      definition: 'Dividing existing shares into multiple shares to lower the price per share.',
      koreanDef: '기존 주식을 여러 개로 나누어 주당 가격을 낮추는 것입니다.',
      example: 'A 2-for-1 split turns 1 share at $200 into 2 shares at $100 each.'
    },
    {
      category: 'market',
      term: 'Short Selling (공매도)',
      korean: '쇼트 셀링 / 공매도',
      definition: 'Borrowing shares to sell, hoping to buy back at a lower price.',
      koreanDef: '주식을 빌려서 팔고, 가격이 하락하면 다시 사서 차익을 얻는 방식입니다.',
      example: 'Short sellers profit when stock prices fall.'
    },
    {
      category: 'market',
      term: 'Index (지수)',
      korean: '인덱스 / 지수',
      definition: 'A measurement of stock market performance, tracking a group of stocks.',
      koreanDef: '여러 주식의 성과를 추적하여 시장 전체의 움직임을 나타내는 지표입니다.',
      example: 'The S&P 500 tracks 500 large US companies.'
    },
    {
      category: 'market',
      term: 'ETF (상장지수펀드)',
      korean: 'ETF',
      definition: 'Exchange-Traded Fund - a basket of securities that trades like a stock.',
      koreanDef: '주식처럼 거래되는 여러 증권의 묶음입니다.',
      example: 'SPY is an ETF that tracks the S&P 500 index.'
    },

    // Risk & Strategy
    {
      category: 'strategy',
      term: 'Diversification (분산투자)',
      korean: '다이버시피케이션',
      definition: 'Spreading investments across different assets to reduce risk.',
      koreanDef: '위험을 줄이기 위해 여러 자산에 투자를 분산하는 것입니다.',
      example: 'Don\'t put all your eggs in one basket - diversify your portfolio.'
    },
    {
      category: 'strategy',
      term: 'Dollar-Cost Averaging (정액분할매수)',
      korean: '달러 코스트 애버리징',
      definition: 'Investing fixed amounts regularly regardless of price.',
      koreanDef: '가격에 관계없이 정기적으로 일정 금액을 투자하는 방식입니다.',
      example: 'Investing $100 monthly in the S&P 500 regardless of market conditions.'
    },
    {
      category: 'strategy',
      term: 'Blue Chip (우량주)',
      korean: '블루칩',
      definition: 'Stock of a well-established, financially sound company.',
      koreanDef: '재정적으로 안정적이고 신뢰할 수 있는 대기업의 주식입니다.',
      example: 'Companies like Apple, Microsoft, and Johnson & Johnson are blue chips.'
    },
    {
      category: 'strategy',
      term: 'Volatility (변동성)',
      korean: '볼라틸리티',
      definition: 'The degree of variation in stock price over time.',
      koreanDef: '주가가 시간에 따라 변동하는 정도입니다.',
      example: 'High volatility stocks can have large price swings in short periods.'
    },
    {
      category: 'strategy',
      term: 'Support Level (지지선)',
      korean: '서포트 레벨',
      definition: 'A price level where a stock tends to stop falling and bounce back.',
      koreanDef: '주가가 하락하다가 반등하는 경향이 있는 가격 수준입니다.',
      example: 'The stock has strong support at $50.'
    },
    {
      category: 'strategy',
      term: 'Resistance Level (저항선)',
      korean: '레지스턴스 레벨',
      definition: 'A price level where a stock tends to stop rising.',
      koreanDef: '주가가 상승하다가 멈추는 경향이 있는 가격 수준입니다.',
      example: 'The stock faces resistance at $100.'
    },

    // Additional Basic Terms
    {
      category: 'basic',
      term: 'Broker (브로커)',
      korean: '브로커 / 증권사',
      definition: 'A person or firm that acts as an intermediary between a buyer and seller for a commission.',
      koreanDef: '매수자와 매도자 사이에서 중개 역할을 하는 개인이나 회사입니다.',
      example: 'Popular brokers include Fidelity, Charles Schwab, and Robinhood.'
    },
    {
      category: 'basic',
      term: 'Commission (수수료)',
      korean: '커미션 / 수수료',
      definition: 'A fee charged by a broker for executing a trade.',
      koreanDef: '브로커가 거래 실행에 대해 부과하는 수수료입니다.',
      example: 'Many brokers now offer commission-free trading for stocks.'
    },
    {
      category: 'basic',
      term: 'Liquidity (유동성)',
      korean: '리퀴디티 / 유동성',
      definition: 'How easily an asset can be bought or sold without affecting its price.',
      koreanDef: '자산을 가격에 영향을 주지 않고 얼마나 쉽게 사고 팔 수 있는지를 나타냅니다.',
      example: 'Large-cap stocks typically have high liquidity.'
    },
    {
      category: 'basic',
      term: 'Asset Allocation (자산배분)',
      korean: '애셋 얼로케이션',
      definition: 'The process of dividing investments among different asset categories like stocks, bonds, and cash.',
      koreanDef: '주식, 채권, 현금 등 다양한 자산 범주에 투자를 나누는 과정입니다.',
      example: 'A common allocation is 60% stocks, 30% bonds, 10% cash.'
    },

    // Additional Trading Terms
    {
      category: 'trading',
      term: 'Stop-Loss Order (손절매 주문)',
      korean: '스탑 로스 오더',
      definition: 'An order to sell a stock when it reaches a certain price to limit losses.',
      koreanDef: '손실을 제한하기 위해 특정 가격에 도달하면 주식을 매도하는 주문입니다.',
      example: 'I set a stop-loss at $90 to limit my potential loss to 10%.'
    },
    {
      category: 'trading',
      term: 'Bid-Ask Spread (매수-매도 스프레드)',
      korean: '비드-애스크 스프레드',
      definition: 'The difference between the highest price a buyer is willing to pay and the lowest price a seller will accept.',
      koreanDef: '매수자가 지불하려는 최고가와 매도자가 받으려는 최저가의 차이입니다.',
      example: 'A narrow bid-ask spread indicates high liquidity.'
    },
    {
      category: 'trading',
      term: 'Volume (거래량)',
      korean: '볼륨 / 거래량',
      definition: 'The number of shares traded during a given period.',
      koreanDef: '특정 기간 동안 거래된 주식의 수량입니다.',
      example: 'High volume often indicates strong investor interest.'
    },
    {
      category: 'trading',
      term: 'After-Hours Trading (시간외 거래)',
      korean: '애프터 아워스 트레이딩',
      definition: 'Trading that occurs outside regular market hours (9:30 AM - 4:00 PM ET).',
      koreanDef: '정규 거래 시간(오전 9시 30분 - 오후 4시) 외에 이루어지는 거래입니다.',
      example: 'Earnings reports released after hours can cause significant price movements.'
    },
    {
      category: 'trading',
      term: 'Gap Up/Down (갭 상승/하락)',
      korean: '갭 업/다운',
      definition: 'When a stock opens significantly higher or lower than its previous close.',
      koreanDef: '주식이 전일 종가보다 훨씬 높거나 낮게 시작하는 것입니다.',
      example: 'The stock gapped up 15% after positive earnings news.'
    },

    // Additional Financial Metrics
    {
      category: 'metrics',
      term: 'Book Value (장부가치)',
      korean: '북 밸류',
      definition: 'The net asset value of a company, calculated as total assets minus intangible assets and liabilities.',
      koreanDef: '회사의 순자산 가치로, 총 자산에서 무형자산과 부채를 뺀 값입니다.',
      example: 'Price-to-book ratio compares stock price to book value per share.'
    },
    {
      category: 'metrics',
      term: 'Free Cash Flow (잉여현금흐름)',
      korean: '프리 캐시 플로우',
      definition: 'Cash generated by operations minus capital expenditures.',
      koreanDef: '영업활동으로 창출된 현금에서 자본 지출을 뺀 금액입니다.',
      example: 'Strong free cash flow indicates a company can fund growth and dividends.'
    },
    {
      category: 'metrics',
      term: 'Debt-to-Equity Ratio (부채비율)',
      korean: '뎁트-투-에퀴티 레이쇼',
      definition: 'A measure of a company\'s financial leverage, calculated by dividing total liabilities by shareholder equity.',
      koreanDef: '총 부채를 자기자본으로 나눈 재무 레버리지 지표입니다.',
      example: 'A ratio above 2.0 might indicate high financial risk.'
    },
    {
      category: 'metrics',
      term: 'Beta (베타)',
      korean: '베타',
      definition: 'A measure of a stock\'s volatility relative to the overall market.',
      koreanDef: '전체 시장 대비 주식의 변동성을 측정하는 지표입니다.',
      example: 'A beta of 1.5 means the stock is 50% more volatile than the market.'
    },
    {
      category: 'metrics',
      term: 'Revenue (매출)',
      korean: '레버뉴 / 매출',
      definition: 'The total amount of money a company receives from its business activities.',
      koreanDef: '회사가 사업 활동으로 벌어들인 총 금액입니다.',
      example: 'Apple\'s quarterly revenue exceeded $100 billion.'
    },

    // Additional Market Concepts
    {
      category: 'market',
      term: 'Market Capitalization Tiers (시총 구분)',
      korean: '마켓 캡 티어스',
      definition: 'Classification of companies: Large-cap ($10B+), Mid-cap ($2B-$10B), Small-cap (under $2B).',
      koreanDef: '기업 분류: 대형주(100억 달러 이상), 중형주(20억-100억), 소형주(20억 미만)',
      example: 'Large-cap stocks are generally less volatile than small-cap stocks.'
    },
    {
      category: 'market',
      term: 'Sector (섹터)',
      korean: '섹터 / 업종',
      definition: 'A group of stocks in the same industry or business area.',
      koreanDef: '같은 산업이나 사업 분야의 주식 그룹입니다.',
      example: 'The 11 main sectors include Technology, Healthcare, and Finance.'
    },
    {
      category: 'market',
      term: 'Bull vs Bear (강세 vs 약세)',
      korean: '불 vs 베어',
      definition: 'Bulls expect prices to rise; Bears expect prices to fall.',
      koreanDef: '불(강세론자)은 가격 상승을 예상하고, 베어(약세론자)는 하락을 예상합니다.',
      example: 'Warren Buffett is generally bullish on the long-term market.'
    },
    {
      category: 'market',
      term: 'Margin Trading (마진 거래)',
      korean: '마진 트레이딩',
      definition: 'Borrowing money from a broker to purchase stock.',
      koreanDef: '브로커로부터 돈을 빌려 주식을 구매하는 것입니다.',
      example: 'Margin trading amplifies both gains and losses.'
    },
    {
      category: 'market',
      term: 'Options (옵션)',
      korean: '옵션',
      definition: 'Contracts that give the right, but not obligation, to buy or sell stock at a specific price.',
      koreanDef: '특정 가격에 주식을 사거나 팔 수 있는 권리를 부여하는 계약입니다.',
      example: 'Call options profit when stock prices rise; put options profit when they fall.'
    },
    {
      category: 'market',
      term: 'Futures (선물)',
      korean: '퓨처스 / 선물',
      definition: 'Contracts to buy or sell an asset at a predetermined price at a future date.',
      koreanDef: '미래의 특정 날짜에 미리 정한 가격으로 자산을 사거나 파는 계약입니다.',
      example: 'S&P 500 futures indicate how the market might open.'
    },

    // Additional Strategy & Risk Terms
    {
      category: 'strategy',
      term: 'Value Investing (가치투자)',
      korean: '밸류 인베스팅',
      definition: 'Investing in undervalued stocks that trade below their intrinsic value.',
      koreanDef: '내재 가치보다 낮게 거래되는 저평가 주식에 투자하는 전략입니다.',
      example: 'Warren Buffett is famous for his value investing approach.'
    },
    {
      category: 'strategy',
      term: 'Growth Investing (성장투자)',
      korean: '그로스 인베스팅',
      definition: 'Investing in companies expected to grow at an above-average rate.',
      koreanDef: '평균 이상의 성장이 예상되는 기업에 투자하는 전략입니다.',
      example: 'Tech startups are often targets for growth investors.'
    },
    {
      category: 'strategy',
      term: 'Dividend Investing (배당투자)',
      korean: '디비던드 인베스팅',
      definition: 'Focusing on stocks that regularly pay dividends for steady income.',
      koreanDef: '안정적인 수입을 위해 정기적으로 배당을 지급하는 주식에 집중하는 전략입니다.',
      example: 'Dividend aristocrats have increased dividends for 25+ consecutive years.'
    },
    {
      category: 'strategy',
      term: 'Risk Tolerance (위험감수도)',
      korean: '리스크 톨러런스',
      definition: 'The degree of variability in investment returns an investor is willing to withstand.',
      koreanDef: '투자자가 감수할 수 있는 투자 수익의 변동성 정도입니다.',
      example: 'Young investors typically have higher risk tolerance than retirees.'
    },
    {
      category: 'strategy',
      term: 'Rebalancing (리밸런싱)',
      korean: '리밸런싱',
      definition: 'Periodically adjusting portfolio allocations back to target percentages.',
      koreanDef: '포트폴리오 배분을 정기적으로 목표 비율로 조정하는 것입니다.',
      example: 'Many investors rebalance quarterly or annually.'
    },
    {
      category: 'strategy',
      term: 'Market Timing (시장 타이밍)',
      korean: '마켓 타이밍',
      definition: 'Attempting to predict market movements to buy low and sell high.',
      koreanDef: '시장 움직임을 예측하여 저가 매수, 고가 매도를 시도하는 것입니다.',
      example: 'Most experts advise against trying to time the market.'
    },
    {
      category: 'strategy',
      term: 'Buy and Hold (장기보유)',
      korean: '바이 앤 홀드',
      definition: 'A passive investment strategy of buying stocks and holding them long-term.',
      koreanDef: '주식을 매수한 후 장기간 보유하는 수동적 투자 전략입니다.',
      example: 'Buy and hold investors ignore short-term market fluctuations.'
    },
    {
      category: 'strategy',
      term: 'Moving Average (이동평균)',
      korean: '무빙 애버리지',
      definition: 'A technical indicator that smooths price data by calculating the average price over a specific period.',
      koreanDef: '특정 기간 동안의 평균 가격을 계산하여 가격 데이터를 평탄화하는 기술적 지표입니다.',
      example: 'The 50-day and 200-day moving averages are widely watched indicators.'
    },

    // More Basic Concepts
    {
      category: 'basic',
      term: 'Quote (시세)',
      korean: '쿼트 / 시세',
      definition: 'The most recent price at which a stock has traded.',
      koreanDef: '주식이 가장 최근에 거래된 가격입니다.',
      example: 'I checked the real-time quote before placing my order.'
    },
    {
      category: 'basic',
      term: 'Lot (매매단위)',
      korean: '랏 / 매매단위',
      definition: 'A standardized number of shares used as the basic unit for trading.',
      koreanDef: '거래의 기본 단위로 사용되는 표준화된 주식 수입니다.',
      example: 'In the US, you can buy any number of shares, unlike some markets with fixed lots.'
    },
    {
      category: 'basic',
      term: 'Trading Hours (거래시간)',
      korean: '트레이딩 아워스',
      definition: 'Regular US market hours are 9:30 AM to 4:00 PM Eastern Time, Monday-Friday.',
      koreanDef: '미국 정규 시장 거래시간은 월요일-금요일 동부시간 오전 9시 30분부터 오후 4시까지입니다.',
      example: 'Pre-market trading starts at 4:00 AM and after-hours extends to 8:00 PM ET.'
    },
    {
      category: 'basic',
      term: 'Settlement (결제)',
      korean: '세틀먼트 / 결제',
      definition: 'The process of transferring securities and cash, typically T+2 (trade date plus 2 days).',
      koreanDef: '증권과 현금을 이전하는 과정으로, 일반적으로 거래일 기준 2일 후(T+2)입니다.',
      example: 'If you sell stock on Monday, the cash settles in your account on Wednesday.'
    },

    // Advanced Trading Concepts
    {
      category: 'trading',
      term: 'Wash Sale (워시세일)',
      korean: '워시 세일',
      definition: 'Selling a security at a loss and repurchasing the same or substantially identical security within 30 days.',
      koreanDef: '손실로 증권을 팔고 30일 이내에 동일하거나 실질적으로 동일한 증권을 재구매하는 것입니다.',
      example: 'Wash sales are not allowed for tax loss harvesting purposes.'
    },
    {
      category: 'trading',
      term: 'Pattern Day Trader (PDT)',
      korean: 'PDT / 패턴 데이 트레이더',
      definition: 'A trader who executes 4+ day trades within 5 business days in a margin account.',
      koreanDef: '마진 계좌에서 5 영업일 이내에 4회 이상의 데이 트레이드를 실행하는 트레이더입니다.',
      example: 'PDT accounts require a minimum balance of $25,000.'
    },
    {
      category: 'trading',
      term: 'Circuit Breaker (서킷브레이커)',
      korean: '서킷 브레이커',
      definition: 'Trading halts triggered when markets fall by certain thresholds (7%, 13%, 20%).',
      koreanDef: '시장이 특정 임계값(7%, 13%, 20%)만큼 하락할 때 발동되는 거래 중단입니다.',
      example: 'Circuit breakers pause trading to prevent panic selling.'
    },
    {
      category: 'trading',
      term: 'Fill or Kill Order (FOK)',
      korean: 'FOK 주문',
      definition: 'An order that must be executed immediately in its entirety or canceled.',
      koreanDef: '즉시 전량 체결되거나 취소되어야 하는 주문입니다.',
      example: 'FOK orders are used when you need certainty about complete execution.'
    },
    {
      category: 'trading',
      term: 'VWAP (거래량가중평균가)',
      korean: 'VWAP',
      definition: 'Volume-Weighted Average Price - average price weighted by volume throughout the day.',
      koreanDef: '하루 동안 거래량으로 가중치를 둔 평균 가격입니다.',
      example: 'Institutional traders often try to execute near VWAP.'
    },

    // More Financial Metrics
    {
      category: 'metrics',
      term: 'EBITDA (이비따)',
      korean: 'EBITDA',
      definition: 'Earnings Before Interest, Taxes, Depreciation, and Amortization.',
      koreanDef: '이자, 세금, 감가상각 및 무형자산상각 전 이익입니다.',
      example: 'EBITDA shows operational profitability without financing effects.'
    },
    {
      category: 'metrics',
      term: 'Profit Margin (이익률)',
      korean: '프라핏 마진',
      definition: 'Net income divided by revenue, showing what percentage of sales becomes profit.',
      koreanDef: '순이익을 매출로 나눈 값으로, 매출의 몇 %가 이익이 되는지 보여줍니다.',
      example: 'Tech companies often have higher profit margins than retailers.'
    },
    {
      category: 'metrics',
      term: 'PEG Ratio (PEG 비율)',
      korean: 'PEG 레이쇼',
      definition: 'P/E ratio divided by earnings growth rate, accounting for growth in valuation.',
      koreanDef: 'P/E 비율을 수익 성장률로 나눈 값으로, 성장을 고려한 가치평가입니다.',
      example: 'A PEG ratio under 1.0 may indicate undervaluation relative to growth.'
    },
    {
      category: 'metrics',
      term: 'Price-to-Sales (P/S)',
      korean: 'P/S 비율',
      definition: 'Stock price divided by revenue per share.',
      koreanDef: '주가를 주당 매출로 나눈 비율입니다.',
      example: 'P/S is useful for valuing companies without profits yet.'
    },
    {
      category: 'metrics',
      term: 'Current Ratio (유동비율)',
      korean: '커런트 레이쇼',
      definition: 'Current assets divided by current liabilities, measuring short-term financial health.',
      koreanDef: '유동자산을 유동부채로 나눈 값으로, 단기 재무 건전성을 측정합니다.',
      example: 'A current ratio above 1.5 is generally considered healthy.'
    },

    // More Market Concepts
    {
      category: 'market',
      term: 'ADR (미국예탁증서)',
      korean: 'ADR',
      definition: 'American Depositary Receipt - allows US investors to buy foreign stocks on US exchanges.',
      koreanDef: '미국 투자자가 미국 거래소에서 외국 주식을 살 수 있게 하는 예탁증서입니다.',
      example: 'Samsung and Toyota trade as ADRs on US markets.'
    },
    {
      category: 'market',
      term: 'REIT (리츠)',
      korean: 'REIT / 리츠',
      definition: 'Real Estate Investment Trust - company that owns income-producing real estate.',
      koreanDef: '수익을 창출하는 부동산을 소유한 회사입니다.',
      example: 'REITs must distribute 90% of taxable income as dividends.'
    },
    {
      category: 'market',
      term: 'Mutual Fund (뮤추얼펀드)',
      korean: '뮤추얼 펀드',
      definition: 'Pooled investment fund managed by professionals, priced once daily.',
      koreanDef: '전문가가 관리하는 공동 투자 펀드로, 하루에 한 번 가격이 책정됩니다.',
      example: 'Unlike ETFs, mutual funds trade at end-of-day NAV.'
    },
    {
      category: 'market',
      term: 'Preferred Stock (우선주)',
      korean: '프리퍼드 스톡 / 우선주',
      definition: 'Stock with priority for dividends and liquidation, but usually no voting rights.',
      koreanDef: '배당과 청산 시 우선권이 있지만 일반적으로 의결권이 없는 주식입니다.',
      example: 'Preferred stocks behave more like bonds than common stocks.'
    },
    {
      category: 'market',
      term: 'Penny Stock (페니스톡)',
      korean: '페니 스톡 / 초저가주',
      definition: 'Stocks trading below $5 per share, often highly speculative.',
      koreanDef: '주당 5달러 미만으로 거래되는 주식으로, 매우 투기적인 경우가 많습니다.',
      example: 'Penny stocks carry high risk due to low liquidity and volatility.'
    },
    {
      category: 'market',
      term: 'SPAC (스팩)',
      korean: 'SPAC / 스팩',
      definition: 'Special Purpose Acquisition Company - blank check company that raises money to acquire another company.',
      koreanDef: '다른 회사를 인수하기 위해 자금을 모으는 백지수표회사입니다.',
      example: 'Many startups went public via SPAC mergers in 2020-2021.'
    },

    // Advanced Strategy Terms
    {
      category: 'strategy',
      term: 'Contrarian Investing (역발상 투자)',
      korean: '컨트래리언 인베스팅',
      definition: 'Investing against prevailing market sentiment.',
      koreanDef: '주류 시장 심리에 반대되는 투자를 하는 것입니다.',
      example: 'Buy when others are fearful, sell when others are greedy.'
    },
    {
      category: 'strategy',
      term: 'Momentum Investing (모멘텀 투자)',
      korean: '모멘텀 인베스팅',
      definition: 'Investing in stocks showing strong recent performance, expecting the trend to continue.',
      koreanDef: '최근 강한 성과를 보인 주식에 투자하며 추세가 계속될 것으로 기대하는 전략입니다.',
      example: 'Momentum investors ride the wave of trending stocks.'
    },
    {
      category: 'strategy',
      term: 'Index Fund (인덱스펀드)',
      korean: '인덱스 펀드',
      definition: 'A fund designed to track a specific market index like the S&P 500.',
      koreanDef: 'S&P 500 같은 특정 시장 지수를 추종하도록 설계된 펀드입니다.',
      example: 'Index funds offer broad diversification at low cost.'
    },
    {
      category: 'strategy',
      term: 'Stop-Limit Order (스탑리밋 주문)',
      korean: '스탑 리밋 오더',
      definition: 'Combines stop and limit orders - triggers at stop price but only fills at limit price or better.',
      koreanDef: '스탑과 리밋 주문의 결합 - 스탑 가격에서 발동되지만 리밋 가격 이상에서만 체결됩니다.',
      example: 'Provides more control than a stop-loss but may not execute in fast markets.'
    },
    {
      category: 'strategy',
      term: 'Tax-Loss Harvesting (세금손실수확)',
      korean: '택스 로스 하베스팅',
      definition: 'Selling securities at a loss to offset capital gains for tax purposes.',
      koreanDef: '세금 목적으로 자본 이득을 상쇄하기 위해 손실로 증권을 매도하는 것입니다.',
      example: 'Harvest losses in December to reduce that year\'s tax bill.'
    },
    {
      category: 'strategy',
      term: 'Dollar-Cost Averaging (DCA)',
      korean: 'DCA',
      definition: 'Investing fixed amounts at regular intervals regardless of share price.',
      koreanDef: '주가에 관계없이 정기적으로 일정 금액을 투자하는 전략입니다.',
      example: 'DCA reduces the risk of investing a lump sum at a market peak.'
    },
    {
      category: 'strategy',
      term: 'Earnings Season (실적시즌)',
      korean: '어닝스 시즌',
      definition: 'Period when most companies report quarterly earnings (Jan, Apr, Jul, Oct).',
      koreanDef: '대부분의 기업이 분기 실적을 보고하는 기간(1월, 4월, 7월, 10월)입니다.',
      example: 'Stock volatility often increases during earnings season.'
    },
    {
      category: 'strategy',
      term: 'Ex-Dividend Date (배당락일)',
      korean: '엑스-디비던드 데이트',
      definition: 'The date by which you must own stock to receive the upcoming dividend.',
      koreanDef: '다가오는 배당을 받기 위해 주식을 소유해야 하는 날짜입니다.',
      example: 'Stock price typically drops by dividend amount on ex-dividend date.'
    },
    {
      category: 'strategy',
      term: 'Dead Cat Bounce (데드캣바운스)',
      korean: '데드캣 바운스',
      definition: 'Temporary recovery in a declining stock before resuming downward trend.',
      koreanDef: '하락 추세에서 일시적으로 반등한 후 다시 하락하는 현상입니다.',
      example: 'Don\'t mistake a dead cat bounce for a true reversal.'
    },
    {
      category: 'strategy',
      term: 'Correction (조정)',
      korean: '코렉션 / 조정',
      definition: 'A decline of 10% or more from recent highs.',
      koreanDef: '최근 고점에서 10% 이상 하락하는 것입니다.',
      example: 'Market corrections are normal and happen regularly.'
    },
    {
      category: 'strategy',
      term: 'Averaging Down (물타기)',
      korean: '애버리징 다운 / 물타기',
      definition: 'Buying more shares as price falls to lower average cost.',
      koreanDef: '가격이 하락할 때 더 많은 주식을 사서 평균 단가를 낮추는 것입니다.',
      example: 'Averaging down can be risky if the stock continues falling.'
    },

    // Corporate Actions & Shareholder Terms
    {
      category: 'market',
      term: 'Stock Split (주식분할)',
      korean: '스톡 스플릿 / 주식분할',
      definition: 'When a company divides existing shares into multiple shares to lower the price per share.',
      koreanDef: '회사가 기존 주식을 여러 주식으로 나누어 주당 가격을 낮추는 것입니다.',
      example: 'In a 2-for-1 split, if you owned 100 shares at $200, you now own 200 shares at $100.'
    },
    {
      category: 'market',
      term: 'Reverse Stock Split (주식병합)',
      korean: '리버스 스톡 스플릿 / 주식병합',
      definition: 'When a company combines multiple shares into fewer shares to increase the price per share.',
      koreanDef: '회사가 여러 주식을 합쳐서 주당 가격을 올리는 것입니다.',
      example: 'In a 1-for-10 reverse split, 100 shares at $5 become 10 shares at $50.'
    },
    {
      category: 'basic',
      term: 'Shareholder (주주)',
      korean: '셰어홀더 / 주주',
      definition: 'A person or entity that owns shares in a company.',
      koreanDef: '회사의 주식을 소유한 개인이나 단체입니다.',
      example: 'As a shareholder, you have voting rights in major company decisions.'
    },
    {
      category: 'basic',
      term: 'Stockholder Equity (주주자본)',
      korean: '스톡홀더 에퀴티 / 주주자본',
      definition: 'The residual interest in assets after deducting liabilities, representing shareholders\' ownership.',
      koreanDef: '부채를 제외한 자산의 잔여 가치로, 주주의 소유권을 나타냅니다.',
      example: 'Stockholder equity appears on the balance sheet as total assets minus total liabilities.'
    },
    {
      category: 'basic',
      term: 'Voting Rights (의결권)',
      korean: '보팅 라이츠 / 의결권',
      definition: 'The right of shareholders to vote on corporate matters and board elections.',
      koreanDef: '주주가 기업 문제와 이사회 선출에 투표할 수 있는 권리입니다.',
      example: 'Common stock typically comes with one vote per share.'
    },
    {
      category: 'basic',
      term: 'Proxy Vote (위임투표)',
      korean: '프록시 보트 / 위임투표',
      definition: 'A vote cast by one person on behalf of a shareholder who cannot attend a meeting.',
      koreanDef: '주주총회에 참석하지 못하는 주주를 대신해 다른 사람이 행사하는 투표입니다.',
      example: 'You can submit your proxy vote online before the annual meeting.'
    },
    {
      category: 'market',
      term: 'IPO (기업공개)',
      korean: 'IPO / 기업공개',
      definition: 'Initial Public Offering - when a private company first sells shares to the public.',
      koreanDef: '비상장 기업이 처음으로 주식을 대중에게 판매하는 것입니다.',
      example: 'Airbnb went public through an IPO in December 2020.'
    },
    {
      category: 'market',
      term: 'Secondary Offering (유상증자)',
      korean: '세컨더리 오퍼링 / 유상증자',
      definition: 'When a company issues additional shares after the IPO.',
      koreanDef: 'IPO 이후 회사가 추가로 주식을 발행하는 것입니다.',
      example: 'Secondary offerings can dilute existing shareholders\' ownership.'
    },
    {
      category: 'market',
      term: 'Share Buyback (자사주매입)',
      korean: '셰어 바이백 / 자사주매입',
      definition: 'When a company repurchases its own shares from the marketplace.',
      koreanDef: '회사가 시장에서 자신의 주식을 다시 사들이는 것입니다.',
      example: 'Apple has spent billions on share buybacks to return value to shareholders.'
    },
    {
      category: 'basic',
      term: 'Outstanding Shares (발행주식수)',
      korean: '아웃스탠딩 셰어스 / 발행주식수',
      definition: 'The total number of shares currently held by all shareholders.',
      koreanDef: '현재 모든 주주가 보유하고 있는 총 주식 수입니다.',
      example: 'Market cap is calculated by multiplying share price by outstanding shares.'
    },
    {
      category: 'basic',
      term: 'Float (유통주식수)',
      korean: '플로트 / 유통주식수',
      definition: 'The number of shares available for public trading, excluding locked-in shares.',
      koreanDef: '제한 주식을 제외하고 일반 대중이 거래할 수 있는 주식 수입니다.',
      example: 'Low float stocks can be more volatile due to limited supply.'
    },
    {
      category: 'metrics',
      term: 'Dilution (희석)',
      korean: '다일루션 / 희석',
      definition: 'Reduction in existing shareholders\' ownership percentage due to new share issuance.',
      koreanDef: '신주 발행으로 인해 기존 주주의 소유 비율이 감소하는 것입니다.',
      example: 'Stock options and secondary offerings can cause dilution.'
    },
    {
      category: 'basic',
      term: 'Annual Meeting (주주총회)',
      korean: '애뉴얼 미팅 / 주주총회',
      definition: 'A yearly gathering where shareholders vote on company matters and meet management.',
      koreanDef: '주주들이 회사 문제에 투표하고 경영진을 만나는 연례 모임입니다.',
      example: 'Important decisions like board elections happen at the annual meeting.'
    },
    {
      category: 'basic',
      term: 'Board of Directors (이사회)',
      korean: '보드 오브 디렉터스 / 이사회',
      definition: 'A group elected by shareholders to oversee company management.',
      koreanDef: '주주들이 선출하여 회사 경영을 감독하는 그룹입니다.',
      example: 'The board of directors hires and oversees the CEO.'
    },
    {
      category: 'market',
      term: 'Spin-off (기업분할)',
      korean: '스핀오프 / 기업분할',
      definition: 'When a company creates a new independent company by separating part of its business.',
      koreanDef: '회사가 사업의 일부를 분리하여 새로운 독립 회사를 만드는 것입니다.',
      example: 'PayPal was spun off from eBay in 2015.'
    },
    {
      category: 'market',
      term: 'Merger & Acquisition (인수합병)',
      korean: 'M&A / 인수합병',
      definition: 'When one company purchases or combines with another company.',
      koreanDef: '한 회사가 다른 회사를 인수하거나 합병하는 것입니다.',
      example: 'Disney acquired 21st Century Fox in a major M&A deal.'
    },
    {
      category: 'metrics',
      term: 'Book Value Per Share (주당순자산)',
      korean: 'BVPS / 주당순자산',
      definition: 'Company\'s equity divided by the number of outstanding shares.',
      koreanDef: '회사의 자기자본을 발행주식수로 나눈 값입니다.',
      example: 'If book value per share exceeds stock price, the stock may be undervalued.'
    },
    {
      category: 'basic',
      term: 'Par Value (액면가)',
      korean: '파 밸류 / 액면가',
      definition: 'The nominal or face value of a stock as stated in the corporate charter.',
      koreanDef: '회사 정관에 명시된 주식의 명목 가치입니다.',
      example: 'Par value is mostly symbolic and usually set very low, like $0.01 per share.'
    },
    {
      category: 'strategy',
      term: 'Insider Trading (내부자거래)',
      korean: '인사이더 트레이딩 / 내부자거래',
      definition: 'Trading based on material non-public information; illegal when done improperly.',
      koreanDef: '공개되지 않은 중요 정보를 기반으로 한 거래로, 부적절할 경우 불법입니다.',
      example: 'Company executives must report their trades to avoid illegal insider trading.'
    },
    {
      category: 'basic',
      term: 'Form 10-K (연간보고서)',
      korean: '폼 10-K / 연간보고서',
      definition: 'Annual report filed with SEC containing comprehensive company information.',
      koreanDef: 'SEC에 제출하는 포괄적인 회사 정보가 담긴 연간 보고서입니다.',
      example: 'Reading the 10-K helps investors understand a company\'s financial health.'
    },
    {
      category: 'basic',
      term: 'Form 10-Q (분기보고서)',
      korean: '폼 10-Q / 분기보고서',
      definition: 'Quarterly report filed with SEC containing unaudited financial statements.',
      koreanDef: 'SEC에 제출하는 미감사 재무제표가 포함된 분기 보고서입니다.',
      example: 'Companies file 10-Q reports three times a year (Q1, Q2, Q3).'
    },

    // Extended Trading & Order Types
    {
      category: 'trading',
      term: 'All or None Order (AON)',
      korean: 'AON 주문',
      definition: 'An order that must be filled completely or not at all.',
      koreanDef: '전량 체결되거나 아예 체결되지 않아야 하는 주문입니다.',
      example: 'AON orders prevent partial fills but may take longer to execute.'
    },
    {
      category: 'trading',
      term: 'Good Till Cancelled (GTC)',
      korean: 'GTC 주문',
      definition: 'An order that remains active until executed or cancelled by the trader.',
      koreanDef: '체결되거나 트레이더가 취소할 때까지 유효한 주문입니다.',
      example: 'GTC orders can stay open for up to 90 days on most platforms.'
    },
    {
      category: 'trading',
      term: 'Day Order (당일주문)',
      korean: '데이 오더',
      definition: 'An order that expires at the end of the trading day if not filled.',
      koreanDef: '체결되지 않으면 거래일 종료시 만료되는 주문입니다.',
      example: 'Most orders are day orders by default unless specified otherwise.'
    },
    {
      category: 'trading',
      term: 'Immediate or Cancel (IOC)',
      korean: 'IOC 주문',
      definition: 'An order to buy or sell immediately, canceling any unfilled portion.',
      koreanDef: '즉시 매매하고 체결되지 않은 부분은 취소하는 주문입니다.',
      example: 'IOC orders allow partial fills unlike FOK orders.'
    },
    {
      category: 'trading',
      term: 'Trailing Stop (트레일링 스탑)',
      korean: '트레일링 스탑',
      definition: 'A stop order that moves with the stock price to lock in profits.',
      koreanDef: '주가를 따라 움직이며 이익을 보호하는 스탑 주문입니다.',
      example: 'Set a trailing stop at 10% to protect gains as price rises.'
    },
    {
      category: 'trading',
      term: 'Iceberg Order (아이스버그 주문)',
      korean: '아이스버그 오더',
      definition: 'A large order divided into smaller visible portions to hide total size.',
      koreanDef: '전체 규모를 숨기기 위해 작은 부분만 보이게 나눈 대량 주문입니다.',
      example: 'Institutional traders use iceberg orders to avoid moving the market.'
    },
    {
      category: 'trading',
      term: 'Scalping (스캘핑)',
      korean: '스캘핑',
      definition: 'A trading strategy of making many small profits from frequent trades.',
      koreanDef: '빈번한 거래로 작은 이익을 많이 만드는 트레이딩 전략입니다.',
      example: 'Scalpers may hold positions for seconds or minutes.'
    },
    {
      category: 'trading',
      term: 'Position Trading (포지션트레이딩)',
      korean: '포지션 트레이딩',
      definition: 'Long-term trading approach holding positions for months or years.',
      koreanDef: '몇 달에서 몇 년간 포지션을 보유하는 장기 트레이딩 접근법입니다.',
      example: 'Position traders focus on fundamental analysis over technical patterns.'
    },

    // Advanced Metrics & Analysis (duplicates removed - already defined earlier)
    {
      category: 'metrics',
      term: 'Return on Assets (ROA)',
      korean: 'ROA / 총자산이익률',
      definition: 'Net income divided by total assets, measuring asset efficiency.',
      koreanDef: '순이익을 총자산으로 나눈 자산 효율성 지표입니다.',
      example: 'ROA shows how efficiently a company uses its assets to generate profit.'
    },
    {
      category: 'metrics',
      term: 'Return on Investment (ROI)',
      korean: 'ROI / 투자수익률',
      definition: 'Gain from investment divided by cost of investment.',
      koreanDef: '투자 이익을 투자 비용으로 나눈 값입니다.',
      example: 'If you invest $1,000 and gain $200, your ROI is 20%.'
    },
    {
      category: 'metrics',
      term: 'Price-to-Book Ratio (P/B)',
      korean: 'P/B 비율',
      definition: 'Stock price divided by book value per share.',
      koreanDef: '주가를 주당 장부가치로 나눈 비율입니다.',
      example: 'A P/B ratio under 1.0 may indicate undervaluation.'
    },
    {
      category: 'metrics',
      term: 'Payout Ratio (배당성향)',
      korean: '페이아웃 레이쇼',
      definition: 'Percentage of earnings paid out as dividends.',
      koreanDef: '배당금으로 지급되는 수익의 비율입니다.',
      example: 'A 60% payout ratio means 60% of profits go to dividends.'
    },
    {
      category: 'metrics',
      term: 'Operating Margin (영업이익률)',
      korean: '오퍼레이팅 마진',
      definition: 'Operating income divided by revenue.',
      koreanDef: '영업이익을 매출로 나눈 비율입니다.',
      example: 'Higher operating margins indicate better operational efficiency.'
    },
    {
      category: 'metrics',
      term: 'Gross Margin (매출총이익률)',
      korean: '그로스 마진',
      definition: 'Gross profit divided by revenue.',
      koreanDef: '매출총이익을 매출로 나눈 비율입니다.',
      example: 'Gross margin shows profitability before operating expenses.'
    },
    {
      category: 'metrics',
      term: 'Quick Ratio (당좌비율)',
      korean: '퀵 레이쇼',
      definition: 'Liquid assets divided by current liabilities.',
      koreanDef: '유동자산을 유동부채로 나눈 비율입니다.',
      example: 'Quick ratio excludes inventory to show immediate liquidity.'
    },
    {
      category: 'metrics',
      term: 'Cash Flow Per Share (주당현금흐름)',
      korean: '캐시 플로우 퍼 셰어',
      definition: 'Operating cash flow divided by outstanding shares.',
      koreanDef: '영업현금흐름을 발행주식수로 나눈 값입니다.',
      example: 'Cash flow per share shows actual cash generation ability.'
    },
    {
      category: 'metrics',
      term: 'Enterprise Value (EV)',
      korean: 'EV / 기업가치',
      definition: 'Market cap plus debt minus cash, representing total company value.',
      koreanDef: '시가총액에 부채를 더하고 현금을 뺀 총 기업가치입니다.',
      example: 'EV is used in EV/EBITDA valuation ratios.'
    },
    {
      category: 'metrics',
      term: 'Price/Earnings to Growth (PEG)',
      korean: 'PEG 비율',
      definition: 'P/E ratio divided by earnings growth rate.',
      koreanDef: 'P/E 비율을 수익 성장률로 나눈 값입니다.',
      example: 'PEG ratio under 1 suggests stock is undervalued relative to growth.'
    },

    // Market Indicators & Indices
    {
      category: 'market',
      term: 'S&P 500',
      korean: 'S&P 500',
      definition: 'Index of 500 large US companies, representing overall market performance.',
      koreanDef: '미국 대형주 500개 기업의 지수로, 전체 시장 성과를 나타냅니다.',
      example: 'The S&P 500 is considered the best gauge of the US stock market.'
    },
    {
      category: 'market',
      term: 'Dow Jones Industrial Average (DJIA)',
      korean: '다우존스 산업평균지수',
      definition: 'Price-weighted index of 30 large US companies.',
      koreanDef: '미국 대기업 30개의 가격가중 지수입니다.',
      example: 'The Dow is one of the oldest and most-watched market indicators.'
    },
    {
      category: 'market',
      term: 'NASDAQ Composite',
      korean: '나스닥 종합지수',
      definition: 'Index of all stocks listed on the NASDAQ exchange.',
      koreanDef: 'NASDAQ 거래소에 상장된 모든 주식의 지수입니다.',
      example: 'NASDAQ is heavily weighted toward technology stocks.'
    },
    {
      category: 'market',
      term: 'Russell 2000',
      korean: '러셀 2000',
      definition: 'Index of 2,000 small-cap US stocks.',
      koreanDef: '미국 소형주 2,000개의 지수입니다.',
      example: 'Russell 2000 performance indicates small-cap stock health.'
    },
    {
      category: 'market',
      term: 'VIX (Volatility Index)',
      korean: 'VIX / 변동성지수',
      definition: 'Measure of market volatility expectations, known as the "fear index".',
      koreanDef: '시장 변동성 예상치로, "공포지수"로 알려져 있습니다.',
      example: 'High VIX indicates investors expect significant market swings.'
    },
    {
      category: 'market',
      term: 'Growth Stock (성장주)',
      korean: '그로스 스톡',
      definition: 'Stock expected to grow faster than the overall market.',
      koreanDef: '전체 시장보다 빠르게 성장할 것으로 예상되는 주식입니다.',
      example: 'Growth stocks typically reinvest earnings rather than pay dividends.'
    },
    {
      category: 'market',
      term: 'Value Stock (가치주)',
      korean: '밸류 스톡',
      definition: 'Stock trading below its intrinsic value.',
      koreanDef: '내재가치보다 낮게 거래되는 주식입니다.',
      example: 'Value stocks often have low P/E ratios and high dividend yields.'
    },
    {
      category: 'market',
      term: 'Cyclical Stock (경기순환주)',
      korean: '사이클리컬 스톡',
      definition: 'Stock whose performance follows economic cycles.',
      koreanDef: '경기 순환에 따라 성과가 달라지는 주식입니다.',
      example: 'Airlines and hotels are cyclical stocks that rise in good economies.'
    },
    {
      category: 'market',
      term: 'Defensive Stock (방어주)',
      korean: '디펜시브 스톡',
      definition: 'Stock that performs well regardless of economic conditions.',
      koreanDef: '경기 상황에 관계없이 좋은 성과를 내는 주식입니다.',
      example: 'Utilities and consumer staples are defensive stocks.'
    },

    // Advanced Strategy & Concepts (duplicates removed)
    {
      category: 'strategy',
      term: 'Long Position (롱포지션)',
      korean: '롱 포지션',
      definition: 'Buying and holding stock expecting price to rise.',
      koreanDef: '가격 상승을 기대하며 주식을 사서 보유하는 것입니다.',
      example: 'Going long is the traditional way to invest in stocks.'
    },
    {
      category: 'strategy',
      term: 'Short Position (숏포지션)',
      korean: '숏 포지션',
      definition: 'Selling borrowed shares expecting price to fall.',
      koreanDef: '가격 하락을 기대하며 빌린 주식을 파는 것입니다.',
      example: 'Short positions have unlimited risk if price rises.'
    },
    {
      category: 'strategy',
      term: 'Hedge (헤지)',
      korean: '헤지',
      definition: 'Taking offsetting position to reduce risk.',
      koreanDef: '위험을 줄이기 위해 상쇄 포지션을 취하는 것입니다.',
      example: 'Buying put options can hedge against portfolio declines.'
    },
    {
      category: 'strategy',
      term: 'Arbitrage (차익거래)',
      korean: '아비트라지',
      definition: 'Profiting from price differences in different markets.',
      koreanDef: '다른 시장의 가격 차이로 이익을 내는 것입니다.',
      example: 'Buying stock on one exchange and selling on another at higher price.'
    },
    {
      category: 'strategy',
      term: 'Leverage (레버리지)',
      korean: '레버리지',
      definition: 'Using borrowed money to amplify investment returns.',
      koreanDef: '투자 수익을 증폭시키기 위해 빌린 돈을 사용하는 것입니다.',
      example: 'Leverage magnifies both gains and losses.'
    },
    {
      category: 'strategy',
      term: 'Covered Call (커버드콜)',
      korean: '커버드 콜',
      definition: 'Selling call options on stocks you own to generate income.',
      koreanDef: '보유 주식에 대한 콜옵션을 팔아 수입을 만드는 전략입니다.',
      example: 'Covered calls provide income but cap upside potential.'
    },
    {
      category: 'strategy',
      term: 'Protective Put (프로텍티브풋)',
      korean: '프로텍티브 풋',
      definition: 'Buying put options to protect stock holdings from decline.',
      koreanDef: '주식 보유분을 하락으로부터 보호하기 위해 풋옵션을 사는 것입니다.',
      example: 'Protective puts act like insurance for your stock positions.'
    },
    {
      category: 'strategy',
      term: 'Straddle (스트래들)',
      korean: '스트래들',
      definition: 'Buying both call and put options at same strike price.',
      koreanDef: '같은 행사가격에 콜과 풋 옵션을 모두 사는 전략입니다.',
      example: 'Straddles profit from large price movements in either direction.'
    },

    // Technical Analysis Terms (duplicate Support Level removed - already defined earlier)
    {
      category: 'strategy',
      term: 'Breakout (돌파)',
      korean: '브레이크아웃',
      definition: 'Price movement beyond established support or resistance.',
      koreanDef: '확립된 지지선이나 저항선을 넘어서는 가격 움직임입니다.',
      example: 'A breakout above $100 could signal further gains.'
    },
    {
      category: 'strategy',
      term: 'Candlestick Chart (캔들스틱차트)',
      korean: '캔들스틱 차트',
      definition: 'Chart showing open, high, low, and close prices as candlesticks.',
      koreanDef: '시가, 고가, 저가, 종가를 캔들 모양으로 보여주는 차트입니다.',
      example: 'Green candles show price increases, red candles show decreases.'
    },
    {
      category: 'strategy',
      term: 'RSI (Relative Strength Index)',
      korean: 'RSI / 상대강도지수',
      definition: 'Momentum indicator measuring overbought/oversold conditions (0-100).',
      koreanDef: '과매수/과매도 상태를 측정하는 모멘텀 지표(0-100)입니다.',
      example: 'RSI above 70 suggests overbought, below 30 suggests oversold.'
    },
    {
      category: 'strategy',
      term: 'MACD (이동평균수렴확산)',
      korean: 'MACD',
      definition: 'Moving Average Convergence Divergence - trend-following indicator.',
      koreanDef: '추세를 따르는 지표로, 이동평균의 수렴과 확산을 보여줍니다.',
      example: 'MACD crossovers signal potential buy or sell opportunities.'
    },
    {
      category: 'strategy',
      term: 'Bollinger Bands (볼린저밴드)',
      korean: '볼린저 밴드',
      definition: 'Volatility bands plotted above and below moving average.',
      koreanDef: '이동평균 위아래에 그려지는 변동성 밴드입니다.',
      example: 'Price touching upper band may indicate overbought conditions.'
    },
    {
      category: 'strategy',
      term: 'Head and Shoulders (헤드앤숄더)',
      korean: '헤드 앤 숄더스',
      definition: 'Chart pattern signaling potential trend reversal.',
      koreanDef: '추세 반전을 신호하는 차트 패턴입니다.',
      example: 'Head and shoulders pattern often precedes a bearish reversal.'
    },
    {
      category: 'strategy',
      term: 'Double Top/Bottom (더블탑/바텀)',
      korean: '더블 탑/바텀',
      definition: 'Chart pattern showing two peaks or troughs at similar levels.',
      koreanDef: '비슷한 수준에서 두 개의 고점이나 저점을 보여주는 차트 패턴입니다.',
      example: 'Double top suggests bearish reversal, double bottom suggests bullish.'
    },
    {
      category: 'strategy',
      term: 'Golden Cross (골든크로스)',
      korean: '골든 크로스',
      definition: 'Short-term moving average crosses above long-term average (bullish signal).',
      koreanDef: '단기 이동평균이 장기 이동평균을 상향 돌파하는 강세 신호입니다.',
      example: '50-day MA crossing above 200-day MA is a golden cross.'
    },
    {
      category: 'strategy',
      term: 'Death Cross (데드크로스)',
      korean: '데드 크로스',
      definition: 'Short-term moving average crosses below long-term average (bearish signal).',
      koreanDef: '단기 이동평균이 장기 이동평균을 하향 돌파하는 약세 신호입니다.',
      example: '50-day MA crossing below 200-day MA is a death cross.'
    },

    // More Basic & Important Terms
    {
      category: 'basic',
      term: 'Ask Price (매도호가)',
      korean: '애스크 프라이스',
      definition: 'Lowest price a seller is willing to accept.',
      koreanDef: '매도자가 받으려는 최저 가격입니다.',
      example: 'If the ask is $50.25, you can buy at that price or higher.'
    },
    {
      category: 'basic',
      term: 'Bid Price (매수호가)',
      korean: '비드 프라이스',
      definition: 'Highest price a buyer is willing to pay.',
      koreanDef: '매수자가 지불하려는 최고 가격입니다.',
      example: 'If the bid is $50.20, you can sell at that price or lower.'
    },
    {
      category: 'basic',
      term: 'Spread (스프레드)',
      korean: '스프레드',
      definition: 'Difference between bid and ask prices.',
      koreanDef: '매수호가와 매도호가의 차이입니다.',
      example: 'Narrow spreads indicate high liquidity.'
    },
    {
      category: 'basic',
      term: 'Market Maker (마켓메이커)',
      korean: '마켓 메이커',
      definition: 'Firm that provides liquidity by quoting buy and sell prices.',
      koreanDef: '매수 및 매도 가격을 제시하여 유동성을 제공하는 회사입니다.',
      example: 'Market makers profit from the bid-ask spread.'
    },
    {
      category: 'basic',
      term: 'Exchange (거래소)',
      korean: '익스체인지',
      definition: 'Marketplace where securities are bought and sold.',
      koreanDef: '증권이 사고 팔리는 시장입니다.',
      example: 'NYSE and NASDAQ are major US stock exchanges.'
    },
    {
      category: 'basic',
      term: 'Listing (상장)',
      korean: '리스팅',
      definition: 'When a company\'s stock becomes available for trading on an exchange.',
      koreanDef: '회사 주식이 거래소에서 거래 가능하게 되는 것입니다.',
      example: 'Companies must meet requirements to maintain their listing.'
    },
    {
      category: 'basic',
      term: 'Delisting (상장폐지)',
      korean: '디리스팅',
      definition: 'Removal of a stock from an exchange.',
      koreanDef: '거래소에서 주식이 제외되는 것입니다.',
      example: 'Stocks can be delisted for failing to meet exchange requirements.'
    },
    {
      category: 'basic',
      term: 'Custody (수탁)',
      korean: '커스터디',
      definition: 'Safekeeping of securities by a financial institution.',
      koreanDef: '금융기관에 의한 증권의 안전한 보관입니다.',
      example: 'Brokers hold stocks in custody on behalf of investors.'
    },
    {
      category: 'basic',
      term: 'Transfer Agent (명의개서대리인)',
      korean: '트랜스퍼 에이전트',
      definition: 'Entity that maintains records of stock ownership.',
      koreanDef: '주식 소유권 기록을 유지하는 기관입니다.',
      example: 'Transfer agents process dividend payments and stock transfers.'
    },
    {
      category: 'basic',
      term: 'Street Name (가두명의)',
      korean: '스트리트 네임',
      definition: 'Stock registered in broker\'s name instead of owner\'s.',
      koreanDef: '소유자가 아닌 브로커 명의로 등록된 주식입니다.',
      example: 'Most brokerage stocks are held in street name for convenience.'
    },

    // Risk & Volatility (duplicate Volatility removed - already defined earlier)
    {
      category: 'strategy',
      term: 'Standard Deviation (표준편차)',
      korean: '스탠다드 데비에이션',
      definition: 'Statistical measure of price volatility.',
      koreanDef: '가격 변동성의 통계적 측정값입니다.',
      example: 'Higher standard deviation indicates greater price dispersion.'
    },
    {
      category: 'strategy',
      term: 'Sharpe Ratio (샤프비율)',
      korean: '샤프 레이쇼',
      definition: 'Risk-adjusted return measure (return minus risk-free rate divided by volatility).',
      koreanDef: '위험조정 수익률 측정값입니다.',
      example: 'Higher Sharpe ratio indicates better risk-adjusted performance.'
    },
    {
      category: 'strategy',
      term: 'Alpha (알파)',
      korean: '알파',
      definition: 'Excess return above benchmark, measuring skill.',
      koreanDef: '벤치마크를 초과하는 수익으로, 실력을 측정합니다.',
      example: 'Positive alpha means outperformance versus the market.'
    },
    {
      category: 'strategy',
      term: 'Drawdown (낙폭)',
      korean: '드로다운',
      definition: 'Peak-to-trough decline during a specific period.',
      koreanDef: '특정 기간 동안 고점에서 저점까지의 하락폭입니다.',
      example: 'A 20% drawdown means portfolio fell 20% from peak.'
    },
    {
      category: 'strategy',
      term: 'Recovery (회복)',
      korean: '리커버리',
      definition: 'Stock price returning to previous high after decline.',
      koreanDef: '하락 후 주가가 이전 고점으로 돌아가는 것입니다.',
      example: 'Full recovery means stock regained all losses.'
    },

    // Corporate Finance & Events
    {
      category: 'market',
      term: 'Earnings Call (실적발표)',
      korean: '어닝스 콜',
      definition: 'Conference call where management discusses quarterly results.',
      koreanDef: '경영진이 분기 실적을 논의하는 컨퍼런스 콜입니다.',
      example: 'Investors listen to earnings calls for company guidance.'
    },
    {
      category: 'market',
      term: 'Guidance (가이던스)',
      korean: '가이던스',
      definition: 'Management\'s forecast for future earnings.',
      koreanDef: '경영진의 미래 수익 전망입니다.',
      example: 'Positive guidance often leads to stock price increases.'
    },
    {
      category: 'market',
      term: 'Earnings Beat (실적상회)',
      korean: '어닝스 비트',
      definition: 'Actual earnings exceeding analyst expectations.',
      koreanDef: '실제 수익이 애널리스트 예상을 초과하는 것입니다.',
      example: 'The stock jumped 10% after earnings beat estimates.'
    },
    {
      category: 'market',
      term: 'Earnings Miss (실적하회)',
      korean: '어닝스 미스',
      definition: 'Actual earnings falling short of expectations.',
      koreanDef: '실제 수익이 예상에 미치지 못하는 것입니다.',
      example: 'Earnings miss led to sharp selloff.'
    },
    {
      category: 'market',
      term: 'Revenue Beat (매출상회)',
      korean: '레버뉴 비트',
      definition: 'Actual revenue exceeding forecasts.',
      koreanDef: '실제 매출이 전망을 초과하는 것입니다.',
      example: 'Strong revenue beat shows growing demand.'
    },
    {
      category: 'market',
      term: 'Restructuring (구조조정)',
      korean: '리스트럭처링',
      definition: 'Major organizational or financial changes to improve efficiency.',
      koreanDef: '효율성을 높이기 위한 주요 조직 또는 재무 변경입니다.',
      example: 'Restructuring often includes layoffs and asset sales.'
    },
    {
      category: 'market',
      term: 'Bankruptcy (파산)',
      korean: '뱅크럽시',
      definition: 'Legal process when company cannot pay debts.',
      koreanDef: '회사가 부채를 갚을 수 없을 때의 법적 절차입니다.',
      example: 'Chapter 11 bankruptcy allows reorganization while Chapter 7 means liquidation.'
    },
    {
      category: 'market',
      term: 'Tender Offer (공개매수)',
      korean: '텐더 오퍼',
      definition: 'Public offer to buy shares at specified price.',
      koreanDef: '지정된 가격에 주식을 사겠다는 공개 제안입니다.',
      example: 'Tender offers often occur during acquisition attempts.'
    },
    {
      category: 'market',
      term: 'Poison Pill (독약조항)',
      korean: '포이즌 필',
      definition: 'Defense tactic making hostile takeover prohibitively expensive.',
      koreanDef: '적대적 인수를 매우 비싸게 만드는 방어 전술입니다.',
      example: 'Poison pills allow existing shareholders to buy discounted shares.'
    },
    {
      category: 'market',
      term: 'White Knight (백기사)',
      korean: '화이트 나이트',
      definition: 'Friendly acquirer saving company from hostile takeover.',
      koreanDef: '적대적 인수로부터 회사를 구하는 우호적 인수자입니다.',
      example: 'The board welcomed the white knight\'s offer.'
    },

    // Investment Vehicles (duplicates ETF and Index Fund removed - already defined earlier)
    {
      category: 'market',
      term: 'Actively Managed Fund (액티브펀드)',
      korean: '액티블리 매니지드 펀드',
      definition: 'Fund where manager actively picks stocks to beat market.',
      koreanDef: '매니저가 시장을 이기기 위해 적극적으로 종목을 선택하는 펀드입니다.',
      example: 'Actively managed funds have higher fees than index funds.'
    },
    {
      category: 'market',
      term: 'Bond (채권)',
      korean: '본드',
      definition: 'Debt security where investor loans money for fixed return.',
      koreanDef: '투자자가 고정 수익을 받고 돈을 빌려주는 채무증권입니다.',
      example: 'Bonds are generally less risky than stocks.'
    },
    {
      category: 'market',
      term: 'Treasury (국채)',
      korean: '트레저리',
      definition: 'Government bond issued by US Treasury.',
      koreanDef: '미국 재무부가 발행하는 국채입니다.',
      example: 'Treasuries are considered risk-free investments.'
    },
    {
      category: 'market',
      term: 'Corporate Bond (회사채)',
      korean: '코퍼레이트 본드',
      definition: 'Debt security issued by corporation.',
      koreanDef: '기업이 발행하는 채무증권입니다.',
      example: 'Corporate bonds offer higher yields than Treasuries but more risk.'
    },
    {
      category: 'market',
      term: 'Convertible Bond (전환사채)',
      korean: '컨버터블 본드',
      definition: 'Bond that can be converted into company stock.',
      koreanDef: '회사 주식으로 전환할 수 있는 채권입니다.',
      example: 'Convertible bonds offer downside protection with upside potential.'
    },
    {
      category: 'market',
      term: 'Warrant (워런트)',
      korean: '워런트',
      definition: 'Right to buy stock at specific price before expiration.',
      koreanDef: '만기 전에 특정 가격에 주식을 살 수 있는 권리입니다.',
      example: 'Warrants are often issued with bonds or preferred stock.'
    },

    // More Trading Concepts
    {
      category: 'trading',
      term: 'Slippage (슬리피지)',
      korean: '슬리피지',
      definition: 'Difference between expected and actual execution price.',
      koreanDef: '예상 체결가와 실제 체결가의 차이입니다.',
      example: 'High slippage occurs in fast-moving or illiquid stocks.'
    },
    {
      category: 'trading',
      term: 'Front Running (선행매매)',
      korean: '프론트 러닝',
      definition: 'Illegal practice of trading ahead of customer orders.',
      koreanDef: '고객 주문보다 먼저 거래하는 불법 행위입니다.',
      example: 'Front running is prohibited by securities regulations.'
    },
    {
      category: 'trading',
      term: 'Pump and Dump (펌프앤덤프)',
      korean: '펌프 앤 덤프',
      definition: 'Fraudulent scheme inflating stock price to sell at profit.',
      koreanDef: '주가를 부풀려 이익을 남기고 파는 사기 수법입니다.',
      example: 'Pump and dump schemes are common with penny stocks.'
    },
    {
      category: 'trading',
      term: 'Churning (과다매매)',
      korean: '처닝',
      definition: 'Excessive trading by broker to generate commissions.',
      koreanDef: '수수료를 벌기 위한 브로커의 과도한 거래입니다.',
      example: 'Churning is unethical and often illegal.'
    },
    {
      category: 'trading',
      term: 'Wash Trading (가장매매)',
      korean: '워시 트레이딩',
      definition: 'Buying and selling same security to create false activity.',
      koreanDef: '허위 활동을 만들기 위해 같은 증권을 사고 파는 것입니다.',
      example: 'Wash trading is illegal market manipulation.'
    },
    {
      category: 'trading',
      term: 'Spoofing (스푸핑)',
      korean: '스푸핑',
      definition: 'Placing fake orders to manipulate prices.',
      koreanDef: '가격을 조작하기 위해 가짜 주문을 내는 것입니다.',
      example: 'Spoofing is illegal and heavily penalized.'
    },

    // Account Types & Tax
    {
      category: 'basic',
      term: 'Brokerage Account (증권계좌)',
      korean: '브로커리지 어카운트',
      definition: 'Account for buying and selling securities.',
      koreanDef: '증권을 사고 팔기 위한 계좌입니다.',
      example: 'Open a brokerage account to start investing in stocks.'
    },
    {
      category: 'basic',
      term: 'Cash Account (현금계좌)',
      korean: '캐시 어카운트',
      definition: 'Account requiring full payment for purchases.',
      koreanDef: '구매에 대한 전액 지불이 필요한 계좌입니다.',
      example: 'Cash accounts cannot use margin or short sell.'
    },
    {
      category: 'basic',
      term: 'Margin Account (마진계좌)',
      korean: '마진 어카운트',
      definition: 'Account allowing borrowing to buy securities.',
      koreanDef: '증권 구매를 위해 돈을 빌릴 수 있는 계좌입니다.',
      example: 'Margin accounts require minimum $2,000 balance.'
    },
    {
      category: 'basic',
      term: 'IRA (Individual Retirement Account)',
      korean: 'IRA / 개인퇴직계좌',
      definition: 'Tax-advantaged retirement savings account.',
      koreanDef: '세금 혜택이 있는 개인 은퇴 저축 계좌입니다.',
      example: 'Traditional IRA contributions may be tax-deductible.'
    },
    {
      category: 'basic',
      term: 'Roth IRA (로스IRA)',
      korean: '로스 IRA',
      definition: 'Retirement account with tax-free withdrawals.',
      koreanDef: '인출 시 세금이 없는 은퇴 계좌입니다.',
      example: 'Roth IRA contributions are made with after-tax dollars.'
    },
    {
      category: 'basic',
      term: '401(k)',
      korean: '401(k)',
      definition: 'Employer-sponsored retirement savings plan.',
      koreanDef: '고용주가 후원하는 은퇴 저축 계획입니다.',
      example: 'Many employers match 401(k) contributions up to certain percentage.'
    },
    {
      category: 'basic',
      term: 'Capital Gains Tax (양도소득세)',
      korean: '캐피털 게인스 택스',
      definition: 'Tax on profits from selling investments.',
      koreanDef: '투자 매각 이익에 대한 세금입니다.',
      example: 'Long-term capital gains are taxed at lower rates than short-term.'
    },
    {
      category: 'basic',
      term: 'Qualified Dividend (적격배당)',
      korean: '퀄리파이드 디비던드',
      definition: 'Dividends taxed at lower capital gains rates.',
      koreanDef: '더 낮은 자본이득 세율로 과세되는 배당입니다.',
      example: 'Most US company dividends are qualified dividends.'
    },
    {
      category: 'basic',
      term: 'Wash Sale Rule (워시세일룰)',
      korean: '워시 세일 룰',
      definition: 'IRS rule preventing tax deduction if you rebuy within 30 days.',
      koreanDef: '30일 이내 재구매 시 세금 공제를 금지하는 IRS 규칙입니다.',
      example: 'Wait 31 days after selling at loss to avoid wash sale rule.'
    },

    // Advanced Market Concepts
    {
      category: 'market',
      term: 'Dark Pool (다크풀)',
      korean: '다크 풀',
      definition: 'Private exchange for trading large blocks anonymously.',
      koreanDef: '대량 거래를 익명으로 하기 위한 비공개 거래소입니다.',
      example: 'Institutional investors use dark pools to avoid moving markets.'
    },
    {
      category: 'market',
      term: 'High-Frequency Trading (HFT)',
      korean: 'HFT / 초단타매매',
      definition: 'Algorithmic trading at extremely high speeds.',
      koreanDef: '매우 빠른 속도의 알고리즘 거래입니다.',
      example: 'HFT firms profit from tiny price differences.'
    },
    {
      category: 'market',
      term: 'Algorithmic Trading (알고리즘트레이딩)',
      korean: '알고리드믹 트레이딩',
      definition: 'Using computer programs to execute trades automatically.',
      koreanDef: '자동으로 거래를 실행하는 컴퓨터 프로그램 사용입니다.',
      example: 'Algorithmic trading accounts for majority of market volume.'
    },
    {
      category: 'market',
      term: 'Block Trade (블록거래)',
      korean: '블록 트레이드',
      definition: 'Large quantity transaction, typically 10,000+ shares.',
      koreanDef: '일반적으로 10,000주 이상의 대량 거래입니다.',
      example: 'Block trades are often negotiated off-exchange.'
    },
    {
      category: 'market',
      term: 'Flash Crash (플래시크래시)',
      korean: '플래시 크래시',
      definition: 'Sudden, severe market drop followed by quick recovery.',
      koreanDef: '갑작스럽고 심각한 시장 하락 후 빠른 회복입니다.',
      example: 'The 2010 flash crash saw Dow drop 1,000 points in minutes.'
    },
    {
      category: 'market',
      term: 'Uptick Rule (업틱룰)',
      korean: '업틱 룰',
      definition: 'Regulation restricting short selling on downticks.',
      koreanDef: '하락 시 공매도를 제한하는 규제입니다.',
      example: 'Uptick rule prevents short sellers from accelerating declines.'
    },
    {
      category: 'market',
      term: 'Reg SHO',
      korean: 'Reg SHO',
      definition: 'SEC regulation governing short selling practices.',
      koreanDef: '공매도 관행을 규제하는 SEC 규정입니다.',
      example: 'Reg SHO requires locating shares before short selling.'
    },
    {
      category: 'market',
      term: 'FINRA',
      korean: 'FINRA',
      definition: 'Financial Industry Regulatory Authority - self-regulatory organization.',
      koreanDef: '금융산업 규제기관 - 자율규제기구입니다.',
      example: 'FINRA oversees broker-dealers and protects investors.'
    },
    {
      category: 'market',
      term: 'SEC (Securities and Exchange Commission)',
      korean: 'SEC / 증권거래위원회',
      definition: 'US federal agency regulating securities markets.',
      koreanDef: '증권시장을 규제하는 미연방 기관입니다.',
      example: 'SEC enforces securities laws and protects investors.'
    },
    {
      category: 'market',
      term: 'EDGAR',
      korean: 'EDGAR',
      definition: 'Electronic database of company filings with SEC.',
      koreanDef: 'SEC에 제출된 기업 서류의 전자 데이터베이스입니다.',
      example: 'Access company 10-K and 10-Q reports through EDGAR.'
    },

    // International & Currency
    {
      category: 'market',
      term: 'GDR (Global Depositary Receipt)',
      korean: 'GDR / 글로벌예탁증서',
      definition: 'Bank certificate representing foreign company shares.',
      koreanDef: '외국 회사 주식을 나타내는 은행 증서입니다.',
      example: 'GDRs allow international trading of foreign stocks.'
    },
    {
      category: 'market',
      term: 'Currency Risk (환율위험)',
      korean: '커런시 리스크',
      definition: 'Risk from exchange rate fluctuations affecting foreign investments.',
      koreanDef: '외국 투자에 영향을 미치는 환율 변동 위험입니다.',
      example: 'Currency risk can enhance or reduce returns on foreign stocks.'
    },
    {
      category: 'market',
      term: 'Emerging Markets (신흥시장)',
      korean: '이머징 마켓',
      definition: 'Developing countries with growing economies.',
      koreanDef: '성장하는 경제를 가진 개발도상국입니다.',
      example: 'Emerging markets offer higher growth potential but more risk.'
    },
    {
      category: 'market',
      term: 'Developed Markets (선진시장)',
      korean: '디벨롭드 마켓',
      definition: 'Mature economies with established financial markets.',
      koreanDef: '확립된 금융시장을 가진 성숙한 경제입니다.',
      example: 'US, UK, and Japan are developed markets.'
    },

    // More Dividend Terms
    {
      category: 'basic',
      term: 'Dividend Aristocrat (배당귀족)',
      korean: '디비던드 아리스토크랫',
      definition: 'S&P 500 company increasing dividends for 25+ consecutive years.',
      koreanDef: '25년 이상 연속으로 배당을 늘린 S&P 500 기업입니다.',
      example: 'Dividend aristocrats demonstrate financial stability.'
    },
    {
      category: 'basic',
      term: 'Dividend King (배당킹)',
      korean: '디비던드 킹',
      definition: 'Company increasing dividends for 50+ consecutive years.',
      koreanDef: '50년 이상 연속으로 배당을 늘린 기업입니다.',
      example: 'Dividend kings are elite income investments.'
    },
    {
      category: 'basic',
      term: 'Special Dividend (특별배당)',
      korean: '스페셜 디비던드',
      definition: 'One-time dividend payment outside regular schedule.',
      koreanDef: '정기 일정 외 일회성 배당금 지급입니다.',
      example: 'Companies pay special dividends when they have excess cash.'
    },
    {
      category: 'basic',
      term: 'Stock Dividend (주식배당)',
      korean: '스톡 디비던드',
      definition: 'Dividend paid in additional shares instead of cash.',
      koreanDef: '현금 대신 추가 주식으로 지급되는 배당입니다.',
      example: 'Stock dividends increase shares owned without tax implications.'
    },
    {
      category: 'basic',
      term: 'DRIP (Dividend Reinvestment Plan)',
      korean: 'DRIP / 배당재투자',
      definition: 'Automatically reinvesting dividends to buy more shares.',
      koreanDef: '배당금을 자동으로 재투자하여 더 많은 주식을 사는 것입니다.',
      example: 'DRIPs compound returns over time without trading fees.'
    },
    {
      category: 'basic',
      term: 'Record Date (배당기준일)',
      korean: '레코드 데이트',
      definition: 'Date determining which shareholders receive dividend.',
      koreanDef: '어떤 주주가 배당을 받을지 결정하는 날짜입니다.',
      example: 'You must own stock on record date to receive dividend.'
    },
    {
      category: 'basic',
      term: 'Payment Date (배당지급일)',
      korean: '페이먼트 데이트',
      definition: 'Date when dividend is actually paid to shareholders.',
      koreanDef: '배당금이 실제로 주주에게 지급되는 날짜입니다.',
      example: 'Payment date is typically several weeks after record date.'
    },

    // Valuation & Analysis
    {
      category: 'metrics',
      term: 'Intrinsic Value (내재가치)',
      korean: '인트린직 밸류',
      definition: 'True underlying value based on fundamentals.',
      koreanDef: '펀더멘털에 기반한 진정한 내재 가치입니다.',
      example: 'Value investors buy when price is below intrinsic value.'
    },
    {
      category: 'metrics',
      term: 'Fair Value (공정가치)',
      korean: '페어 밸류',
      definition: 'Estimated appropriate price for a stock.',
      koreanDef: '주식의 적정 가격 추정치입니다.',
      example: 'Analysts calculate fair value using various models.'
    },
    {
      category: 'metrics',
      term: 'Discounted Cash Flow (DCF)',
      korean: 'DCF / 현금흐름할인',
      definition: 'Valuation method based on projected future cash flows.',
      koreanDef: '예상 미래 현금흐름을 기반으로 한 가치평가 방법입니다.',
      example: 'DCF analysis requires estimating growth rates and discount rates.'
    },
    {
      category: 'metrics',
      term: 'Net Asset Value (NAV)',
      korean: 'NAV / 순자산가치',
      definition: 'Total assets minus liabilities, divided by shares outstanding.',
      koreanDef: '총자산에서 부채를 뺀 후 발행주식수로 나눈 값입니다.',
      example: 'Mutual funds are priced at NAV at end of each trading day.'
    },
    {
      category: 'metrics',
      term: 'Asset Turnover (자산회전율)',
      korean: '애셋 턴오버',
      definition: 'Revenue divided by total assets, measuring efficiency.',
      koreanDef: '매출을 총자산으로 나눈 효율성 측정 지표입니다.',
      example: 'High asset turnover indicates efficient use of assets.'
    },
    {
      category: 'metrics',
      term: 'Inventory Turnover (재고회전율)',
      korean: '인벤토리 턴오버',
      definition: 'Cost of goods sold divided by average inventory.',
      koreanDef: '매출원가를 평균 재고로 나눈 값입니다.',
      example: 'High inventory turnover suggests strong sales.'
    },
    {
      category: 'metrics',
      term: 'Working Capital (운전자본)',
      korean: '워킹 캐피털',
      definition: 'Current assets minus current liabilities.',
      koreanDef: '유동자산에서 유동부채를 뺀 값입니다.',
      example: 'Positive working capital indicates financial health.'
    },
    {
      category: 'metrics',
      term: 'Goodwill (영업권)',
      korean: '굿윌',
      definition: 'Intangible asset from acquiring company above book value.',
      koreanDef: '장부가치 이상으로 회사를 인수할 때 생기는 무형자산입니다.',
      example: 'Goodwill represents brand value and customer relationships.'
    },
    {
      category: 'metrics',
      term: 'Tangible Book Value (유형장부가치)',
      korean: '탠저블 북 밸류',
      definition: 'Book value excluding intangible assets like goodwill.',
      koreanDef: '영업권 같은 무형자산을 제외한 장부가치입니다.',
      example: 'Tangible book value shows true liquidation value.'
    },

    // Market Conditions & Sentiment
    {
      category: 'market',
      term: 'Bull Run (강세장)',
      korean: '불 런',
      definition: 'Extended period of rising stock prices.',
      koreanDef: '주가가 상승하는 장기간의 기간입니다.',
      example: 'The 2010s saw historic bull run in US stocks.'
    },
    {
      category: 'market',
      term: 'Bear Market Rally (약세장반등)',
      korean: '베어 마켓 랠리',
      definition: 'Temporary price increase during overall downtrend.',
      koreanDef: '전반적인 하락 추세 중 일시적인 가격 상승입니다.',
      example: 'Don\'t confuse bear market rally with trend reversal.'
    },
    {
      category: 'market',
      term: 'Sideways Market (횡보장)',
      korean: '사이드웨이즈 마켓',
      definition: 'Market moving within range without clear direction.',
      koreanDef: '명확한 방향 없이 범위 내에서 움직이는 시장입니다.',
      example: 'Sideways markets frustrate both bulls and bears.'
    },
    {
      category: 'market',
      term: 'Capitulation (항복매도)',
      korean: '캐피츄레이션',
      definition: 'Panic selling marking potential market bottom.',
      koreanDef: '잠재적 바닥을 알리는 패닉 매도입니다.',
      example: 'Capitulation often signals buying opportunity.'
    },
    {
      category: 'market',
      term: 'FOMO (Fear of Missing Out)',
      korean: 'FOMO / 기회상실공포',
      definition: 'Anxiety about missing investment opportunity.',
      koreanDef: '투자 기회를 놓칠까 하는 불안감입니다.',
      example: 'FOMO can lead to buying at market tops.'
    },
    {
      category: 'market',
      term: 'FUD (Fear, Uncertainty, Doubt)',
      korean: 'FUD / 공포불확실의심',
      definition: 'Negative information spread to manipulate sentiment.',
      koreanDef: '심리를 조작하기 위해 퍼뜨리는 부정적 정보입니다.',
      example: 'Beware of FUD campaigns targeting stocks.'
    },
    {
      category: 'market',
      term: 'Irrational Exuberance (비이성적과열)',
      korean: '이래셔널 이그주버런스',
      definition: 'Excessive market optimism detached from fundamentals.',
      koreanDef: '펀더멘털과 동떨어진 과도한 시장 낙관입니다.',
      example: 'Greenspan warned of irrational exuberance in 1996.'
    },
    {
      category: 'market',
      term: 'Flight to Quality (안전자산선호)',
      korean: '플라이트 투 퀄리티',
      definition: 'Moving investments to safer assets during uncertainty.',
      koreanDef: '불확실성 시기에 더 안전한 자산으로 투자를 옮기는 것입니다.',
      example: 'Flight to quality drives money into bonds and blue chips.'
    },
    {
      category: 'market',
      term: 'Risk On/Risk Off (위험선호/회피)',
      korean: '리스크 온/오프',
      definition: 'Market sentiment toward risky vs safe assets.',
      koreanDef: '위험자산 대 안전자산에 대한 시장 심리입니다.',
      example: 'Risk-on days favor stocks, risk-off days favor bonds.'
    },
    {
      category: 'market',
      term: 'Breadth (시장폭)',
      korean: '브레드스',
      definition: 'Number of stocks participating in market move.',
      koreanDef: '시장 움직임에 참여하는 종목 수입니다.',
      example: 'Strong breadth confirms healthy market trend.'
    }
  ];

  const categories = [
    { id: 'all', name: '전체', icon: BookOpen },
    { id: 'basic', name: '기본 용어', icon: Award },
    { id: 'trading', name: '거래', icon: TrendingUp },
    { id: 'metrics', name: '재무 지표', icon: BarChart3 },
    { id: 'market', name: '시장', icon: Globe },
    { id: 'strategy', name: '전략 & 리스크', icon: DollarSign }
  ];

  // Filter terms based on search and category
  const filteredTerms = stockTerms.filter(term => {
    const matchesCategory = selectedCategory === 'all' || term.category === selectedCategory;
    const matchesSearch = searchTerm === '' ||
      term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.korean.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleExpand = (term) => {
    setExpandedTerm(expandedTerm === term ? null : term);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-7 h-7" />
            <h1 className="text-2xl font-bold">미주공부방</h1>
          </div>
          <p className="text-base text-blue-100">US Stock Market Study Room - Learn Essential Stock Market Terminology</p>
          <p className="text-sm text-blue-200 mt-1">미국 주식 투자의 필수 용어를 배워보세요</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search terms in English or Korean... (예: Stock, 주식, P/E)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:outline-none text-sm"
            />
          </div>
        </div>

        {/* Category Filter */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {categories.map(category => {
              const Icon = category.icon;
              const isSelected = selectedCategory === category.id;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition ${
                    isSelected
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {category.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-gray-600 text-sm">
            {filteredTerms.length}개의 용어를 찾았습니다
          </p>
        </div>

        {/* Terms List */}
        <div className="space-y-3">
          {filteredTerms.map((term, index) => {
            const isExpanded = expandedTerm === term.term;
            return (
              <div
                key={index}
                className="bg-white rounded-lg shadow hover:shadow-lg transition overflow-hidden border border-gray-200"
              >
                <button
                  onClick={() => toggleExpand(term.term)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition"
                >
                  <div className="flex items-center gap-3 flex-1 text-left">
                    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                      {term.term[0]}
                    </div>
                    <div>
                      <h3 className="text-base font-bold text-gray-900">{term.term}</h3>
                      <p className="text-sm text-gray-600">{term.korean}</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>

                {isExpanded && (
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    <div className="space-y-3">
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-1.5 flex items-center gap-2 text-sm">
                          <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                          English Definition
                        </h4>
                        <p className="text-gray-800 text-sm leading-relaxed pl-3.5">{term.definition}</p>
                      </div>

                      <div>
                        <h4 className="font-semibold text-gray-700 mb-1.5 flex items-center gap-2 text-sm">
                          <span className="w-1.5 h-1.5 bg-purple-500 rounded-full"></span>
                          한글 설명
                        </h4>
                        <p className="text-gray-800 text-sm leading-relaxed pl-3.5">{term.koreanDef}</p>
                      </div>

                      {term.example && (
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-3 rounded-lg">
                          <h4 className="font-semibold text-gray-700 mb-1.5 flex items-center gap-2 text-sm">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                            Example
                          </h4>
                          <p className="text-gray-700 italic text-sm pl-3.5">{term.example}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {filteredTerms.length === 0 && (
          <div className="text-center py-12">
            <Search className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <h3 className="text-base font-semibold text-gray-600 mb-1">No terms found</h3>
            <p className="text-sm text-gray-500">Try a different search term or category</p>
          </div>
        )}
      </div>
    </div>
  );
}
